import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Stack } from '@mui/material';
import { IconPhoto, IconVideo, IconUpload } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { validateMediaFile } from '../../../utils/mediaUploader';

const MediaDropzone = ({ 
  onMediaAdd, 
  maxFiles = 8, 
  disabled = false 
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (disabled) return;

    console.log('📥 Files dropped:', acceptedFiles.map(f => ({ 
      name: f.name, 
      size: f.size, 
      type: f.type 
    })));

    // 파일 개수 제한 검사
    if (acceptedFiles.length > maxFiles) {
      alert(t('postThreads.media.validation.tooManyFiles', { maxFiles }));
      return;
    }

    // 파일 검증 및 에러 수집
    const validFiles = [];
    const errors = [];

    acceptedFiles.forEach(file => {
      console.log('🔍 Validating file:', { 
        name: file.name, 
        size: file.size, 
        type: file.type 
      });
      
      const validation = validateMediaFile(file, t);
      
      if (validation.valid) {
        validFiles.push(file);
        console.log('✅ File validation passed:', file.name);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
        console.warn('❌ File validation failed:', file.name, validation.error);
      }
    });

    // 에러가 있으면 alert 표시
    if (errors.length > 0) {
      const errorMessage = errors.join('\n\n');
      alert(errorMessage);
    }

    // 유효한 파일이 있으면 추가
    if (validFiles.length > 0) {
      onMediaAdd(validFiles);
    }

    // react-dropzone에서 거부된 파일들 처리
    if (rejectedFiles.length > 0) {
      const rejectedErrors = rejectedFiles.map(fileRejection => {
        const fileName = fileRejection.file.name;
        const errorCodes = fileRejection.errors.map(e => e.code);
        
        if (errorCodes.includes('file-invalid-type')) {
          return `${fileName}: ${t('postThreads.media.validation.unsupportedFormat')}`;
        }
        if (errorCodes.includes('file-too-large')) {
          return `${fileName}: ${t('postThreads.media.validation.fileTooLarge')}`;
        }
        return `${fileName}: ${t('postThreads.media.validation.uploadFailed')}`;
      });
      
      alert(rejectedErrors.join('\n\n'));
    }
  }, [onMediaAdd, disabled, maxFiles, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // Threads API에서 지원하는 정확한 MIME 타입만 허용
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov']
    },
    maxFiles,
    disabled,
    multiple: true
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? theme.palette.primary.main : theme.palette.grey[300],
        borderRadius: theme.spacing(2),
        padding: theme.spacing(3),
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
        opacity: disabled ? 0.5 : 1,
        '&:hover': disabled ? {} : {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.action.hover
        }
      }}
    >
      <input {...getInputProps()} />
      <Stack spacing={2} alignItems="center">
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <IconPhoto size={32} color={theme.palette.grey[600]} />
          <IconVideo size={32} color={theme.palette.grey[600]} />
          <IconUpload size={32} color={theme.palette.grey[600]} />
        </Box>
        
        <Typography variant="body2" color="text.primary">
          {isDragActive 
            ? t('postThreads.media.dropzone.dragActive')
            : t('postThreads.media.dropzone.dragInactive')
          }
        </Typography>
        
        <Typography variant="caption" color="text.secondary">
          {t('postThreads.media.dropzone.supportedFormats')}
        </Typography>
        
        <Typography variant="caption" color="text.secondary">
          최대 {maxFiles}개 파일 • 동영상은 최대 300MB
        </Typography>
      </Stack>
    </Box>
  );
};

export default MediaDropzone;